import userAgentParser from "@/utils/user-agent-parser";
import { DeviceOs, DeviceType } from "@/model/models";
import { getOrCreateDeviceIdentifier } from "@/utils/device-identifier";

type AndroidDeviceInfo = {
    android_id?: string;
    manufacturer?: string;
    model?: string;
    android_version?: string;
    sdk_int?: string;
    device?: string;
    product?: string;
    brand?: string;
};

type DeviceContext = {
    device_identifier: string;
    device_type: DeviceType;
    device_os: DeviceOs;
    device_name?: string;
    user_agent?: string;
};

declare global {
    interface Window {
        AndroidBridge?: {
            getDeviceInfo?: () => AndroidDeviceInfo;
        };
    }
}

function normalizeAndroidInfo(raw: any): AndroidDeviceInfo | null {
    if (!raw) return null;

    if (typeof raw === "string") {
        try {
            return JSON.parse(raw) as AndroidDeviceInfo;
        } catch (e) {
            console.warn("Invalid Android device JSON:", raw, e);
            return null;
        }
    }

    return null;
}

export async function resolveDeviceContext(): Promise<DeviceContext> {
    const ua = navigator.userAgent;
    const bridge = (globalThis as any).AndroidBridge;

    if (bridge?.getDeviceInfo) {
        try {
            const raw = bridge.getDeviceInfo();
            const info = normalizeAndroidInfo(raw);

            if (info?.android_id) {
                const deviceName = info.brand?.trim() ? info.brand : info.model?.trim() ? info.model : undefined;
                return {
                    device_identifier: info.android_id,
                    device_type: DeviceType.APP,
                    device_os: DeviceOs.ANDROID_TV,
                    device_name: deviceName,
                    user_agent: typeof info === "string" ? info : JSON.stringify(info),
                };
            }
        } catch (e) {
            console.warn("AndroidBridge failed, falling back to web", e);
        }
    }

    // Web fallback
    const deviceIdentifier = await getOrCreateDeviceIdentifier();
    return {
        device_identifier: deviceIdentifier,
        device_type: userAgentParser.extractDeviceType(ua),
        device_os: userAgentParser.extractDeviceOs(ua),
        user_agent: ua,
    };
}
